/**
 * UserObjectNode - Pill-shaped dark node for AtlassianUser entities.
 *
 * No expand behaviour — stays compact in all states.
 * Selection + distance opacity via GraphSelectionContext.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- This node uses inline styles for dynamic values driven by selection/focus state; will migrate to css prop in a follow-up. */
/* eslint-disable @atlaskit/design-system/use-tokens-typography -- Uses hard-coded typography values for the pill-shaped user node; will migrate to typography tokens in a follow-up. */
/* eslint-disable @atlaskit/design-system/ensure-design-token-usage -- Uses hard-coded colors for the dark pill node; will migrate to design tokens in a follow-up. */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values -- See above. */

import React, { memo, useLayoutEffect, useRef, useState } from 'react';

import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useGraphSelection, computeNodeOpacity } from '../context/GraphSelectionContext';

import { LoadingPerimeterRing, useIsNodeLoadingData } from './LoadingPerimeterRing';
import type { ObjectNodeData } from './ObjectNode';

export type UserObjectNodeType = Node<ObjectNodeData, 'userObjectNode'>;

// Outer corner radius of the user pill. Passed to `LoadingPerimeterRing`
// so the rotating ring follows the pill's curvature (SVG clamps the
// rect's `rx` to half its short side automatically, giving a true pill
// regardless of how wide the host gets).
const USER_PILL_BORDER_RADIUS = 999;

const MONO_FONT = token('font.family.code');

const HANDLE_STYLE: React.CSSProperties = {
	width: 1,
	height: 1,
	opacity: 0,
	border: 'none',
	background: 'transparent',
};

const CURRENT_USER_BG = '#1c2b42';
const CURRENT_USER_BORDER = '#669df1';
const CURRENT_USER_NAME_COLOR = '#669df1';
const CURRENT_USER_SUBTITLE_COLOR = '#96999e';

function getFirstName(fullName: string): string {
	return fullName.split(' ')[0] || fullName;
}

function getTextProperty(
	properties: Record<string, any> | undefined,
	keys: string[],
): string | null {
	for (const key of keys) {
		const value = properties?.[key];
		if (typeof value === 'string' && value.trim()) {
			return value;
		}
	}
	return null;
}

function getBootstrapUserProperties(
	properties: Record<string, any> | undefined,
): Record<string, any> | undefined {
	const bootstrapUser = properties?.bootstrapUser;
	return bootstrapUser && typeof bootstrapUser === 'object'
		? (bootstrapUser as Record<string, any>)
		: undefined;
}

function getUserDisplayName(properties: Record<string, any> | undefined, fallback: string): string {
	const bootstrapName = getTextProperty(getBootstrapUserProperties(properties), [
		'displayName',
		'name',
		'nickname',
	]);
	return fallback === 'You' && bootstrapName ? bootstrapName : fallback;
}

function getUserPictureUrl(
	properties: Record<string, any> | undefined,
	pictureUrl: string | null | undefined,
): string | null {
	return (
		pictureUrl ??
		getTextProperty(properties, ['picture']) ??
		getTextProperty(getBootstrapUserProperties(properties), ['picture'])
	);
}

function getUserSubtitle(properties: Record<string, any> | undefined): string | null {
	const bootstrapProperties = getBootstrapUserProperties(properties);

	return (
		getTextProperty(properties, ['jobTitle', 'title', 'position', 'role', 'roleTitle']) ??
		getTextProperty(bootstrapProperties, ['jobTitle', 'title', 'position', 'role', 'roleTitle'])
	);
}

export const UserObjectNode: React.MemoExoticComponent<
	({ data, selected, id }: NodeProps<UserObjectNodeType>) => JSX.Element
> = memo(({ data, selected, id }: NodeProps<UserObjectNodeType>): JSX.Element => {
	const nodeData = data;
	const graphSelection = useGraphSelection();

	const fullName = getUserDisplayName(nodeData.properties, nodeData.displayName || nodeData.id);
	const firstName = getFirstName(fullName);
	const subtitle = getUserSubtitle(nodeData.properties);
	const pictureUrl = getUserPictureUrl(nodeData.properties, nodeData.pictureUrl);
	const [pictureFailed, setPictureFailed] = React.useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const showPicture = !!pictureUrl && !pictureFailed;

	const isCurrentUser = graphSelection?.centerNodeId === nodeData.id;
	const isActivelySelected =
		graphSelection?.hasActiveSelection === true && graphSelection.activeNodeId === id;
	const displayName = isActivelySelected ? fullName : firstName;
	const displayedSubtitle = isActivelySelected ? subtitle : null;
	const isVisuallyEmphasized = isActivelySelected || selected || isHovered;

	const opacity = computeNodeOpacity(id, graphSelection);

	// Mount-time fade-in. See `ObjectNode.tsx` for rationale; the
	// flag is captured once at mount so the fade doesn't replay
	// when the upstream TTL clears `isAppearing`.
	const appearOnMountRef = useRef<boolean>(nodeData.isAppearing === true);
	const [hasMounted, setHasMounted] = useState(!appearOnMountRef.current);
	useLayoutEffect(() => {
		if (!appearOnMountRef.current) {
			return;
		}
		const raf = requestAnimationFrame(() => setHasMounted(true));
		return () => cancelAnimationFrame(raf);
	}, []);
	const appearOpacity = hasMounted ? 1 : 0;

	// ── Loading-state rotating perimeter border ──────────────────
	// `ExpandNodeAtom` adds this node's id to `ExpandingNodeIdsAtom`
	// while its related-data fetch is in flight; the shared
	// `<LoadingPerimeterRing>` component subscribes to that atom and
	// renders the rotating ring. We mirror the loading bool here
	// only so we can swap the static pill border to transparent —
	// otherwise the static border would peek through under the
	// gradient ring.
	const isLoadingData = useIsNodeLoadingData(id);

	const bgColor = isCurrentUser ? CURRENT_USER_BG : '#101214';
	const inactiveBorderColor = isVisuallyEmphasized ? '#FFFFFF' : '#3D3F43';
	const staticBorderColor = isCurrentUser ? CURRENT_USER_BORDER : inactiveBorderColor;
	const borderColor = isLoadingData ? 'transparent' : staticBorderColor;
	const textColor = isCurrentUser ? CURRENT_USER_NAME_COLOR : '#dddee1';
	const subtitleColor = isCurrentUser ? CURRENT_USER_SUBTITLE_COLOR : '#8C8F97';
	const avatarFallbackBg = isCurrentUser ? '#27415f' : '#52525b';
	const avatarFallbackColor = isCurrentUser ? CURRENT_USER_NAME_COLOR : '#f5f5f5';

	return (
		<div
			style={{
				opacity: opacity * appearOpacity,
				transition: 'opacity 0.3s ease-out',
				position: 'relative',
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsHovered(true)}
			onBlur={() => setIsHovered(false)}
		>
			<Handle type="target" position={Position.Left} style={{ ...HANDLE_STYLE, left: 0 }} />

			{/* Rotating perimeter ring shown while related-data fetches
				    are in flight. Renders iff this node id is in
				    `ExpandingNodeIdsAtom`; sized from ReactFlow's measured
				    node dimensions, so the ring resizes automatically when
				    the pill grows on click (e.g. expanding to show
				    full-name + subtitle). */}
			<LoadingPerimeterRing nodeId={id} borderRadius={USER_PILL_BORDER_RADIUS} />

			<div
				style={{
					backgroundColor: bgColor,
					border: `${isCurrentUser ? 1.5 : 1}px solid ${borderColor}`,
					borderRadius: USER_PILL_BORDER_RADIUS,
					padding: 8,
					cursor: 'pointer',
					fontFamily: MONO_FONT,
					display: 'flex',
					alignItems: 'center',
					gap: 6,
					maxWidth: isActivelySelected ? 360 : undefined,
					whiteSpace: 'nowrap' as const,
					overflow: 'hidden',
					boxShadow: isVisuallyEmphasized
						? '0 0 0 1px rgba(245,247,251,0.45), 0 10px 24px rgba(0,0,0,0.28)'
						: undefined,
					transition: 'border-color 0.1s ease-out, box-shadow 0.1s ease-out',
					// Sit above the loading ring (z-index 0) so the pill
					// content stays fully readable while the ring spins.
					position: 'relative',
					zIndex: 1,
				}}
			>
				{showPicture ? (
					<img
						src={pictureUrl}
						alt=""
						onError={() => setPictureFailed(true)}
						style={{
							width: 24,
							height: 24,
							borderRadius: '50%',
							objectFit: 'cover',
							flexShrink: 0,
							display: 'block',
						}}
					/>
				) : (
					<span
						style={{
							display: 'grid',
							placeItems: 'center',
							width: 24,
							height: 24,
							borderRadius: '50%',
							fontSize: 11,
							fontWeight: 600,
							backgroundColor: avatarFallbackBg,
							color: avatarFallbackColor,
							flexShrink: 0,
						}}
					>
						{fullName.charAt(0)}
					</span>
				)}

				<div
					style={{
						display: 'flex',
						minWidth: 0,
						flexDirection: 'column',
						gap: 2,
						lineHeight: 1,
					}}
				>
					<span
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							minWidth: 0,
							fontSize: 14,
							lineHeight: '14px',
							color: textColor,
						}}
					>
						{displayName}
					</span>
					{displayedSubtitle ? (
						<span
							style={{
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								minWidth: 0,
								fontSize: 12,
								lineHeight: '12px',
								color: subtitleColor,
							}}
						>
							{displayedSubtitle}
						</span>
					) : null}
				</div>
			</div>
			<Handle type="source" position={Position.Right} style={{ ...HANDLE_STYLE, right: 0 }} />
		</div>
	);
});

UserObjectNode.displayName = 'UserObjectNode';
